import { resolveImageAlt } from '../../utils/image-alt-utils';

type RecordValue = Record<string, unknown>;
export type VideoCardFields = {
  videoFieldId: number | null;
  descriptionFieldId: number | null;
  thumbnailFieldId: number | null;
};
export type VideoCard = {
  id: string;
  name: string;
  description: string;
  videoUrl: string;
  videoEmbedUrl: string;
  thumbnailUrl: string;
  thumbnailAlt: string;
};

function record(value: unknown): RecordValue | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;
}
function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function getYoutubeVideoId(value: unknown): string | null {
  if (!text(value)) return null;
  try {
    const url = new URL(text(value));
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port)
      return null;
    const host = url.hostname.toLowerCase();
    const parts = url.pathname.split('/').filter(Boolean);
    let id: string | null = null;
    if (host === 'youtu.be' || host === 'www.youtu.be') {
      if (parts.length === 1) id = parts[0];
    } else if (
      ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host)
    ) {
      if (url.pathname === '/watch') id = url.searchParams.get('v');
      else if (parts.length === 2 && ['embed', 'shorts', 'live'].includes(parts[0])) id = parts[1];
    } else if (['youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
      if (parts.length === 2 && parts[0] === 'embed') id = parts[1];
    }
    return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

function fieldLabel(value: unknown) {
  return text(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
function findField(fields: unknown[], id: number | null, names: string[]) {
  return fields.map(record).find(field => {
    const definition = record(field?.field);
    return id !== null
      ? String(definition?.id) === String(id)
      : names.includes(fieldLabel(definition?.name));
  });
}
function imageUrl(value: unknown) {
  const source = text(record(value)?.url);
  try {
    const url = new URL(source);
    return url.protocol === 'https:' && !url.username && !url.password ? source : null;
  } catch {
    return null;
  }
}

export function toVideoCards(items: unknown, mapping: VideoCardFields): VideoCard[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  return items.flatMap(item => {
    const value = record(item);
    const name = text(value?.name);
    const fields = Array.isArray(value?.fields) ? value.fields : [];
    const video = findField(fields, mapping.videoFieldId, [
      'video',
      'video url',
      'youtube',
      'youtube url',
    ]);
    const videoId = getYoutubeVideoId(video?.value);
    if (!name || !videoId) return [];
    const id =
      typeof value?.id === 'number' || typeof value?.id === 'string'
        ? String(value.id)
        : `${videoId}:${name}`;
    if (seen.has(id)) return [];
    seen.add(id);
    const description = findField(fields, mapping.descriptionFieldId, [
      'predstavenie',
      'predstaveni',
      'popis',
    ]);
    const thumbnail = findField(fields, mapping.thumbnailFieldId, ['nahlad']);
    const thumbnailImage = imageUrl(thumbnail?.image) ? thumbnail?.image : value?.image;
    const thumbnailUrl = imageUrl(thumbnailImage);
    return [
      {
        id,
        name,
        description: text(description?.value),
        videoUrl: `https://www.youtube.com/watch?v=${videoId}`,
        videoEmbedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`,
        thumbnailUrl: thumbnailUrl ?? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        thumbnailAlt: thumbnailUrl ? resolveImageAlt(thumbnailImage, name) : name,
      },
    ];
  });
}
